import { useEffect, useRef } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom';
import './App.css';
import LoginPage from './components/LoginPage';
import { OidcAuthProvider } from './components/OidcAuthProvider';
import { CheckYourEmail } from './pages/CheckYourEmail';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import ProtectedRoute from './components/ProtectedRoute';
import { Questionnaire } from './components/questionnaire/Questionnaire';
import { AISystemsPage } from './pages/AISystemsPage';
import { AISystemDetailPage } from './pages/AISystemDetailPage';
import { Dashboard } from './pages/Dashboard';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { RegistrationPage } from './pages/RegistrationPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { Settings } from './pages/SettingsPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { RegisteredUserProfile } from './pages/RegisteredUserProfile';
import { ExpiredLink } from './pages/ExpiredLink';
import { AccessDenied } from './pages/AccessDenied';
import { WelcomePage } from './pages/WelcomePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { OidcCallbackPage } from './pages/OidcCallbackPage';
import { OidcLoginRedirectPage } from './pages/OidcLoginRedirectPage';
import { LogoutPage } from './pages/LogoutPage';
import { PATHS } from './constants/Paths.constants';
import { TermsAndPrivacyPage } from './pages/TermsAndPrivacyPage';
import { GlobalToast } from './components/ui/Toast/GlobalToast';
import { useAppDispatch } from './hooks/redux';
import { featureFlags, isOidcAuth } from './config/featureFlags';
import { finishDocumentSigning } from './services/DocumentationService';
import { setEUDeclarationStatus } from './store/slices/documentationSlice';
import { showToast } from './store/slices/toastSlice';
import { AppHealthGate } from './components/AppHealthGate';

// Local-auth-only routes are replaced with NotFoundPage when OIDC is active.
const localAuthRoutes = [
  PATHS.LOGIN,
  PATHS.REGISTRATION,
  PATHS.FORGOT_PASSWORD,
  PATHS.RESET_PASSWORD,
  PATHS.CHECK_YOUR_EMAIL,
  PATHS.VERIFY_EMAIL,
  PATHS.EXPIRED_LINK,
];

function App() {
  const oidc = isOidcAuth();

  return (
    <Router>
      <SignatureApiCompletionHandler />
      {oidc ? (
        <OidcAuthProvider>
          <AppInner oidc={oidc} />
        </OidcAuthProvider>
      ) : (
        <AppInner oidc={oidc} />
      )}
    </Router>
  );
}

function AppInner({ oidc }: { oidc: boolean }) {
  return (
    <div className="App">
      <GlobalToast />
      <AppHealthGate>
          <Routes>
            <Route
              path={PATHS.ROOT}
              element={<Navigate to={PATHS.DASHBOARD} replace />}
            />
            {/* Local auth routes — replaced with 404 when OIDC is active */}
            <Route
              path={PATHS.LOGIN}
              element={oidc ? <OidcLoginRedirectPage /> : <LoginPage />}
            />
            <Route
              path={PATHS.REGISTRATION}
              element={
                featureFlags.signUpEnabled ? (
                  <RegistrationPage />
                ) : (
                  <Navigate to={PATHS.LOGIN} replace />
                )
              }
            />
            <Route
              path={PATHS.FORGOT_PASSWORD}
              element={oidc ? <NotFoundPage /> : <ForgotPasswordPage />}
            />
            <Route
              path={PATHS.RESET_PASSWORD}
              element={oidc ? <NotFoundPage /> : <ResetPasswordPage />}
            />
            <Route
              path={PATHS.CHECK_YOUR_EMAIL}
              element={oidc ? <NotFoundPage /> : <CheckYourEmail />}
            />
            <Route
              path={PATHS.VERIFY_EMAIL}
              element={oidc ? <NotFoundPage /> : <VerifyEmailPage />}
            />
            <Route
              path={PATHS.EXPIRED_LINK}
              element={oidc ? <NotFoundPage /> : <ExpiredLink />}
            />
            <Route path={PATHS.ACCESS_DENIED} element={<AccessDenied />} />
            {/* OIDC callback — must be outside ProtectedRoute and never redirected */}
            {oidc && <Route path="/auth/callback" element={<OidcCallbackPage />} />}
            <Route path={PATHS.LOGOUT} element={<LogoutPage />} />
            <Route path="/welcome" element={<WelcomePage />} />
            <Route
              path={PATHS.DASHBOARD}
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path={PATHS.AI_SYSTEMS}
              element={
                <ProtectedRoute>
                  <AISystemsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={`${PATHS.AI_SYSTEM_DETAIL}/*`}
              element={
                <ProtectedRoute>
                  <AISystemDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={PATHS.QUESTIONNAIRE}
              element={
                <ProtectedRoute>
                  <Questionnaire />
                </ProtectedRoute>
              }
            />
            <Route
              path={PATHS.USER_MANAGEMENT}
              element={
                <ProtectedRoute>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={PATHS.USER_PROFILE}
              element={
                <ProtectedRoute>
                  <RegisteredUserProfile />
                </ProtectedRoute>
              }
            />
            {/* <Route
          path={PATHS.AUDIT_LOG}
          element={
            <ProtectedRoute>
              <AuditLogPage />
            </ProtectedRoute>
          }
        /> */}
            <Route
              path={PATHS.SETTINGS}
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            >
              <Route
                index
                element={<Navigate to={PATHS.SETTINGS_ACCOUNT} replace />}
              />
              <Route path="account" element={<></>} />
              <Route path="general" element={<></>} />
            </Route>
            <Route
              path={PATHS.TERMSANDPRIVACY}
              element={<TermsAndPrivacyPage />}
            />
            <Route path="*" element={<Navigate to={PATHS.ROOT} replace />} />
          </Routes>
      </AppHealthGate>
    </div>
  );
}

export default App;

function SignatureApiCompletionHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const activeKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const getAiSystemIdFromPath = (pathname: string): string | null => {
      const match = pathname.match(/^\/ai-systems\/([^/]+)/);
      return match?.[1] ?? null;
    };

    const params = new URLSearchParams(location.search);
    const rawResult = params.get('ceremony_result') ?? params.get('event');

    if (!rawResult) {
      activeKeyRef.current = null;
      return;
    }

    const result = rawResult === 'signing_complete' ? 'completed' : rawResult;

    if (result !== 'completed' && result !== 'ceremony.completed') {
      const message =
        result === 'cancelled' || result === 'canceled'
          ? 'Signing session was cancelled.'
          : 'Signing returned an unexpected state. ' + result;
      dispatch(showToast({ message, type: 'warning' }));
      params.delete('ceremony_result');
      params.delete('event');
      params.delete('documentId');
      params.delete('envelope_id');
      params.delete('envelopeId');
      params.delete('recipient_id');
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : ''
        },
        { replace: true }
      );
      activeKeyRef.current = null;
      return;
    }

    const documentId = params.get('documentId');
    if (!documentId) {
      dispatch(
        showToast({
          message: 'Missing document identifier in signing callback.',
          type: 'error'
        })
      );
      params.delete('ceremony_result');
      params.delete('event');
      params.delete('documentId');
      params.delete('envelope_id');
      params.delete('envelopeId');
      params.delete('recipient_id');
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : ''
        },
        { replace: true }
      );
      activeKeyRef.current = null;
      return;
    }

    const envelopeId = params.get('envelope_id') ?? params.get('envelopeId');
    const key = `${result}-${documentId}-${envelopeId ?? ''}`;
    if (activeKeyRef.current === key) {
      return;
    }
    activeKeyRef.current = key;

    const finalizeSigning = async () => {
      let shouldClearParams = true;
      let nextPathname = location.pathname;
      try {
        const signedDocument = await finishDocumentSigning(documentId, {
          result,
          envelopeId
        });
        dispatch(
          showToast({
            message: 'Document signed successfully.',
            type: 'success'
          })
        );
        dispatch(setEUDeclarationStatus('COMPLETED'));

        const aiSystemId = getAiSystemIdFromPath(location.pathname);
        const isEuDeclarationDocument =
          signedDocument.category === 'EU_DECLARATION_OF_CONFORMITY';

        if (aiSystemId && isEuDeclarationDocument) {
          params.set('openAnnexIVDrawer', '1');
          nextPathname = `/ai-systems/${aiSystemId}/requirement-list`;
        }
      } catch (error: any) {
        shouldClearParams = false;
        const fallback =
          'Failed to finalize the signed document. Please try again.';
        const errorMessage = error?.response?.data?.message ?? fallback;
        dispatch(showToast({ message: errorMessage, type: 'error' }));
      } finally {
        if (shouldClearParams) {
          params.delete('ceremony_result');
          params.delete('event');
          params.delete('documentId');
          params.delete('envelope_id');
          params.delete('envelopeId');
          params.delete('recipient_id');
          const nextSearch = params.toString();
          navigate(
            {
              pathname: nextPathname,
              search: nextSearch ? `?${nextSearch}` : ''
            },
            { replace: true }
          );
        }
        activeKeyRef.current = null;
      }
    };

    void finalizeSigning();
  }, [location.pathname, location.search, navigate, dispatch]);

  return null;
}
